var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LoadingScreen } from "../components/Loading/LoadingScreen";
// eslint-disable-next-line @typescript-eslint/no-empty-function
var NOOP = function () { };
var NOOP_LOADING_STARTER = function () { return ({ endLoading: NOOP }); };
var LoadingContext = createContext({
    startLoading: NOOP_LOADING_STARTER,
    startLockLoading: NOOP_LOADING_STARTER,
    startCoverLoading: NOOP_LOADING_STARTER,
    endLoadingByName: NOOP,
});
var initialLoadingState = {
    backgroundProcesses: [],
    lockingProcesses: [],
    coveringProcesses: [],
};
export var LoadingProvider = function (props) {
    var children = props.children;
    var _a = useState(initialLoadingState), loadingState = _a[0], setLoadingState = _a[1];
    var cover = loadingState.coveringProcesses.length > 0;
    var lock = cover || loadingState.lockingProcesses.length > 0;
    var loading = cover || lock || loadingState.backgroundProcesses.length > 0;
    var loadingStarters = useMemo(function () {
        /**
         * ローディング処理の開始・終のの共通化用関数。
         * processTypeKeyで指定した種類のプロセスの開始処理とその終了関数の生成。
         */
        var startLoadingBase = function (processTypeKey) { return function (processName) {
            var processId = processName == null ? Symbol(processTypeKey) : Symbol.for(processName);
            setLoadingState(function (before) {
                var _a;
                return __assign(__assign({}, before), (_a = {}, _a[processTypeKey] = __spreadArrays(before[processTypeKey], [processId]), _a));
            });
            return {
                endLoading: function () {
                    return setLoadingState(function (before) {
                        var _a;
                        return (__assign(__assign({}, before), (_a = {}, _a[processTypeKey] = before[processTypeKey].filter(function (value) { return value !== processId; }), _a)));
                    });
                },
            };
        }; };
        /**
         * processNameで指定したプロセスを終了
         */
        var endLoadingByName = function (processName) {
            var processId = Symbol.for(processName);
            setLoadingState(function (before) { return ({
                backgroundProcesses: before.backgroundProcesses.filter(function (value) { return value !== processId; }),
                lockingProcesses: before.lockingProcesses.filter(function (value) { return value !== processId; }),
                coveringProcesses: before.coveringProcesses.filter(function (value) { return value !== processId; }),
            }); });
        };
        return {
            startLoading: startLoadingBase('backgroundProcesses'),
            startLockLoading: startLoadingBase('lockingProcesses'),
            startCoverLoading: startLoadingBase('coveringProcesses'),
            endLoadingByName: endLoadingByName,
        };
    }, []);
    return (React.createElement(LoadingContext.Provider, { value: loadingStarters },
        children,
        React.createElement(LoadingScreen, { loading: loading, lockScreen: lock, coverScreen: cover })));
};
/**
 * ローディングを開始する関数を返すHook
 */
export var useLoading = function () { return useContext(LoadingContext); };
/**
 * 宣言的な記述によりローディングを開始・終了するHook
 *
 * 監視対象の変数と使用するローディングの種類を渡すと、その変数の状態が変化したときにローディングを表示する
 *
 * @param watchVariable 監視対象の変数
 * @param loadingFunction 使用するローディングの種類(string)
 */
export var useDeclarativeLoading = function (loadingTypeValue, watchVariable) {
    var _a = useLoading(), startLoading = _a.startLoading, startLockLoading = _a.startLockLoading, startCoverLoading = _a.startCoverLoading;
    var loadingFunction;
    if (loadingTypeValue === 'loading') {
        loadingFunction = startLoading;
    }
    else if (loadingTypeValue === 'lockLoading') {
        loadingFunction = startLockLoading;
    }
    else {
        loadingFunction = startCoverLoading;
    }
    var endLoadingRef = useRef(null);
    useEffect(function () {
        var _a;
        if (watchVariable && (endLoadingRef === null || endLoadingRef === void 0 ? void 0 : endLoadingRef.current) === null) {
            var endLoading = loadingFunction().endLoading;
            endLoadingRef.current = endLoading;
        }
        else if (endLoadingRef.current !== null) {
            endLoadingRef === null || endLoadingRef === void 0 ? void 0 : endLoadingRef.current();
            endLoadingRef.current = null;
        }
        return (_a = endLoadingRef.current) !== null && _a !== void 0 ? _a : undefined;
    }, [loadingFunction, watchVariable]);
};
